import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sun, FileCheck, Scale, ArrowRight } from 'lucide-react';
import { services as mockServices } from '../data/mock';
import { contentAPI } from '../services/api';

const Services = () => {
  const [services, setServices] = useState(mockServices);

  useEffect(() => {
    window.scrollTo(0, 0);
    contentAPI.get('services_pillars').then(res => {
      if (res.data && res.data.pillars) setServices(res.data);
    }).catch(() => {});
  }, []);

  const iconMap = {
    Sun: Sun,
    FileCheck: FileCheck,
    Scale: Scale
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-600 to-emerald-700 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Our Services</h1>
          <p className="text-xl text-emerald-50 max-w-3xl mx-auto leading-relaxed">
            Comprehensive renewable energy solutions tailored to your needs — from consulting to regulatory compliance and legal support
          </p>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {services.pillars.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <div key={service.id} className="text-center">
                  <div className="inline-flex w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl items-center justify-center mb-6 transform hover:scale-110 transition-transform duration-300">
                    <Icon className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h2>
                  <p className="text-gray-600 mb-6 leading-relaxed text-lg">{service.summary}</p>
                  <Link
                    to={service.link}
                    className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-md"
                  >
                    Explore Service
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {/* Renewable Energy Consulting */}
            <div className="bg-white rounded-2xl p-10 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <Sun className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Renewable Energy Consulting</h2>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                End-to-end consulting services for solar, wind, and hybrid renewable energy projects. From feasibility studies to commissioning support, we guide you through every phase of your renewable journey.
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Rooftop & Ground-Mounted Solar</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Captive Power Plants</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Group Captive Models</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">DISCOM PPA Projects</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Virtual PPA Structuring</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Hybrid Wind-Solar Projects</span>
                </li>
              </ul>
              <Link
                to="/renewable-consulting"
                className="inline-flex items-center text-emerald-600 font-semibold hover:text-emerald-700 transition-colors duration-200"
              >
                Learn More
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>

            {/* Regulatory Consultancy */}
            <div className="bg-white rounded-2xl p-10 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <FileCheck className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Regulatory Consultancy</h2>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Navigate complex regulatory approvals with confidence. Our experts handle all statutory clearances, licenses, and compliance requirements for developers and EPC companies.
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Transmission Line Crossings</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Highway Crossing Permissions</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Aviation & Defence Clearances</span>
                </li>
              </ul>
              <Link
                to="/regulatory-consultancy"
                className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors duration-200"
              >
                Learn More
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>

            {/* Legal Advisory */}
            <div className="bg-white rounded-2xl p-10 shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <Scale className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Legal Advisory</h2>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                Expert legal support for licensing, contracts, tariff adoption, and dispute resolution. Protect your interests with our experienced energy law specialists.
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Licensing Requirements</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Tariff Adoption Process</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Contract Adjudication</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Force Majeure & Change in Law</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">PPA Dispute Resolution</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Hypothecation Permissions</span>
                </li>
              </ul>
              <Link
                to="/legal-advisory"
                className="inline-flex items-center text-emerald-600 font-semibold hover:text-emerald-700 transition-colors duration-200"
              >
                Learn More
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Need Expert Guidance?
          </h2>
          <p className="text-xl text-emerald-50 mb-8">
            Let's discuss how we can support your renewable energy project
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-emerald-600 font-semibold rounded-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            Get In Touch
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
